import { ignoredSentryErrors } from './ignoredSentryErrors';
import {
  isTruthy,
  getErrorForLogging,
  isArdoqError,
} from '@ardoq/common-helpers';
import { last, mapValues } from 'lodash';
import { type Logger } from '@ardoq/logging';
import { ArdoqId } from '@ardoq/api-types';
import * as Sentry from '@sentry/browser';
import { sanitizeEvent } from './sanitizeEvent';

/**
 * In some cases we add ArdoqErrors to "extraData" in order to log them in Sentry.
 * In particular, when we in some cases could have multiple errors we create
 * a new Error and attach the ArdoqErrors in "extraData" in order to send the
 * original errors to Sentry in-bulk.
 */
const ardoqErrorsToSentryErrors = <T>(value: T): T | Error | Error[] => {
  if (isArdoqError(value)) return getErrorForLogging(value);
  if (Array.isArray(value)) return value.map(ardoqErrorsToSentryErrors);
  return value;
};

export const getEnvironment = (hostname: string) => {
  const levels = hostname.split('.');

  if (hostname.endsWith('localhost')) {
    return 'localhost';
  }

  if (hostname.endsWith('ardoq.dev') && levels.length >= 3) {
    // E.g. some-org.engagement.ardoq.dev
    return levels.slice(-3)[0];
  }

  if (hostname.endsWith('ardoq.com')) {
    const testDomains = ['test', 'piedpiper-test', 'betatest', 'azure-test'];

    if (testDomains.includes(levels.slice(-3)[0])) {
      // E.g. some-org.test.ardoq.com
      return 'test';
    }

    if (levels.length >= 3) {
      // E.g. some-org.ardoq.com or some-org.us.ardoq.com
      return 'production';
    }
  }

  return 'unknown';
};

export function initialize(
  dsn: string,
  release: string,
  environment: string,
  sampleRate: number,
  sessionStart?: string
) {
  Sentry.init({
    dsn,
    release,
    environment,
    ignoreErrors: ignoredSentryErrors,
    tracePropagationTargets: ['/api/'],
    integrations: [
      ...Sentry.getDefaultIntegrations({}).filter(
        ({ name }) => name !== 'BrowserApiErrors'
      ),
      Sentry.breadcrumbsIntegration({
        dom: {
          // Which attributes to serialize from DOM elements, from the most relevant to least relevant
          serializeAttribute: [
            'data-click-id',
            'data-testid',
            'data-test-id',
            'aria-label',
            'name',
            'title',
            'id',
            'class',
          ],
        },
      }),
      Sentry.browserTracingIntegration({
        beforeStartSpan(context) {
          return {
            ...context,
            // remove id hash from name (e.g. /details/924cffb711ce9cb19f9e0e66 -> /details)
            name: location.pathname.replace(/\/[a-f0-9]{24}/g, ''),
          };
        },
      }),
      Sentry.rewriteFramesIntegration({
        iteratee: frame => {
          // Remove org label from path in order to resolve
          // sourcemaps in Sentry
          // eslint-disable-next-line
          frame.abs_path = frame.abs_path?.replace(
            /\/static\/.*?\//,
            '/static/'
          );
          frame.filename = frame.filename?.replace(
            /\/static\/.*?\//,
            '/static/'
          );
          return frame;
        },
      }),
    ],
    beforeSend(event) {
      if (!event.fingerprint) {
        const { type, value, stacktrace } = event.exception?.values?.[0] ?? {};
        const functionName = last(stacktrace?.frames)?.function;

        event.fingerprint = [type, value, functionName].filter(isTruthy);
      }
      event.extra = {
        ...event.extra,
        sessionStart,
      };
      return sanitizeEvent(event);
    },
    beforeSendTransaction(transaction) {
      return sanitizeEvent(transaction);
    },
    tracesSampleRate: sampleRate,
  });
}

type LogLevel = 'info' | 'warning' | 'error';
type LogParams = [...Parameters<Logger>, LogLevel];
type ExtendedLog = (...args: LogParams) => void;

export const log: ExtendedLog = (ardoqError, message, extraData, level) => {
  Sentry.withScope(scope => {
    const error = isArdoqError(ardoqError) ? ardoqError.error : ardoqError;
    const errorWithoutSpecificName = error.name === 'Error';
    const [sentryTitle, sentrySubtitle] = message
      ? [message, error.message]
      : errorWithoutSpecificName
        ? [error.message, undefined]
        : [error.name, error.message];

    const sentryError = Object.assign(Error(sentrySubtitle), {
      name: sentryTitle,
      stack: error.stack,
    });

    if (extraData) {
      const { tags, fingerprint, ...rest } = extraData;
      if (tags) {
        scope.setTags(tags);
      }
      if (Array.isArray(fingerprint)) {
        scope.setFingerprint(fingerprint);
      }
      const plainExtras = mapValues(rest, ardoqErrorsToSentryErrors);
      scope.setExtras(plainExtras);
    }

    scope.setLevel(level);
    Sentry.captureException(sentryError);
  });
};

const logFn =
  (level: LogLevel): Logger =>
  (error, message, extraData) => {
    log(error, message, extraData, level);
  };

export const logWarn = logFn('warning');
export const logError = logFn('error');

/**
 * Use this to see if a function is used by customers, without having to log as an error or a warning
 */
export const logIfUsed = (functionName: string) => {
  Sentry.captureMessage(`${functionName} is used`);
};

export const updateContext = (
  userId: ArdoqId,
  email: string | null,
  orgId: ArdoqId | null | undefined
) => {
  {
    const userSegment = getUserSegment(email);
    const scope = Sentry.getCurrentScope();
    scope.setUser({ id: userId });

    scope.setTag('user.segment', userSegment);
    scope.setTag('organization.id', orgId);
    scope.setTag('organization.name', getOrganizationName(scope));

    /**
     * @deprecated This should be removed in favor of using tags
     */
    scope.setExtras({ orgId, userSegment });
  }
};

const getUserSegment = (email: string | null) => {
  if (!email) {
    return null;
  }

  if (email.includes('@ardoq.com')) {
    return 'internal';
  }

  return 'external';
};

const getOrganizationName = (scope: Sentry.Scope) => {
  const url = scope.getScopeData().tags.url;

  if (typeof url !== 'string') {
    return null;
  }

  const hostname = new URL(url).hostname;
  const organizationName = hostname.split('.')[0] || null;

  return organizationName;
};

type BreadcrumbExtras = {
  category?: string;
  data?: {
    [key: string]: any;
  };
  level?: Sentry.SeverityLevel;
};

/**
 * Create a breadcrumb which will be buffered and sent with the next error.
 */
export const addBreadcrumb = (message: string, extras?: BreadcrumbExtras) => {
  const { category, data, level = 'info' } = extras || {};
  Sentry.addBreadcrumb({ message, category, data, level });
};

/**
 * Set a tag on the current scope.
 * - Keys have max length of 32 and can contain only a-zA-Z0-9_.:-.
 * - Values have max length of 200 characters and they cannot contain \n
 * Ref. https://docs.sentry.io/platforms/javascript/enriching-events/tags/
 */
export const setSentryTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};
